import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./timesheet-list.style.css";
import { isOwnerOrFinance } from "../../utils/role-check";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import StyledModalView from "../Modal/StyledModalView";

function TimesheetListView({
    timesheets,
    allTimesheets,
    filterStatus,
    setFilterStatus,
    filterUser,
    setFilterUser,
    sortOrder,
    loading,
    error,
    projectRole,
    showDeleteModal,
    setShowDeleteModal,
    confirmDelete,
    handleDeleteConfirmed,
    handleApprove,
    handleReject,
    auth,
    userId,
    voormanProjects,
    filterProject,
    setFilterProject
}) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");

    const getStatusBadge = (status) => {
        const badgeMap = {
            draft: { text: t('timesheets.draft'), class: 'doc-badge doc-missing' },
            pending: { text: t('timesheets.pending'), class: 'doc-badge doc-expired' },
            approved_by_voorman: { text: t('timesheets.approvedByVoorman'), class: 'doc-badge doc-valid' },
            approved: { text: t('timesheets.approved'), class: 'doc-badge doc-valid' },
            rejected: { text: t('timesheets.rejected'), class: 'doc-badge doc-missing' }
        };
        const badge = badgeMap[status] || { text: t('common.unknown'), class: 'doc-badge' };
        return <span className={badge.class}>{badge.text}</span>;
    };

    const getStatusText = (status) => {
        const statusMap = {
            draft: t('timesheets.draft'),
            pending: t('timesheets.pending'),
            approved_by_voorman: t('timesheets.approvedByVoorman'),
            approved: t('timesheets.approved'),
            rejected: t('timesheets.rejected')
        };

        return statusMap[status] || t('common.unknown');
    }

    const showProjectColumn = isOwnerOrFinance(auth?.role) || (projectRole === "voorman");
    const showUserColumn = isOwnerOrFinance(auth?.role) || (projectRole === "voorman");
    const canSeeAll = isOwnerOrFinance(auth?.role) || (projectRole === "voorman");
    const visibleTimesheets = canSeeAll ? timesheets : timesheets.filter(ts => ts.user_id === auth.userId);
    const filteredTimesheets = visibleTimesheets
        .filter(ts => !filterStatus || ts.status === filterStatus)
        .filter(ts => !filterUser || ts.user.name === filterUser)
        .filter(ts => !filterProject || ts.project.name === filterProject)
        .filter(ts => {
            const q = search.toLowerCase();
            return (
                ts.user.name?.toLowerCase().includes(q) ||
                ts.project.name?.toLowerCase().includes(q) ||
                String(ts.week_number).includes(q) ||
                String(ts.year).includes(q)
            );
        })
        .sort((a, b) => sortOrder === "asc" ? a.week_number - b.week_number : b.week_number - a.week_number);

    // Group filteredTimesheets by project_id
    const groupedByProject = filteredTimesheets.reduce((acc, ts) => {
        const pid = ts.project?.id || 'onbekend';
        if (!acc[pid]) acc[pid] = [];
        acc[pid].push(ts);
        return acc;
    }, {});

    // Sort each project's timesheets by date descending (newest first)
    Object.keys(groupedByProject).forEach(pid => {
        groupedByProject[pid].sort((a, b) => {
            const dateA = new Date(a.date || a.created_at || `${a.year}-W${a.week_number}`);
            const dateB = new Date(b.date || b.created_at || `${b.year}-W${b.week_number}`);
            return dateB - dateA;
        });
    });

    // Compute filter options from the full, unfiltered timesheet list (allTimesheets)
    const allUserOptions = Array.from(new Set(allTimesheets.map(ts => ts.user.name)));
    const allProjectOptions = Array.from(new Set(allTimesheets.map(ts => ts.project.name)));

    // Always show filters, even if no results
    const Filters = (
        <section className="filters-bar" aria-label="Timesheet filters">
            <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                    id="timesheet-search"
                    className="search-input"
                    type="search"
                    placeholder={t('timesheets.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    aria-label="Zoek urenstaten"
                />
                {search && (
                    <button
                        type="button"
                        className="clear-btn"
                        onClick={() => setSearch('')}
                        aria-label="Wis zoekopdracht"
                    >
                        ✕
                    </button>
                )}
            </div>
            <div className="filters-right">
                <div className="filter-group">
                    <label htmlFor="status-filter">
                        {t('timesheets.status')}
                    </label>
                    <select
                        id="status-filter"
                        className="filter-select"
                        value={filterStatus || ""}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        aria-label="Filter op status"
                    >
                        <option value="">{t('timesheets.allStatuses')}</option>
                        <option value="draft">{t('timesheets.draft')}</option>
                        <option value="pending">{t('timesheets.pending')}</option>
                        <option value="approved_by_voorman">{t('timesheets.approvedByVoorman')}</option>
                        <option value="approved">{t('timesheets.approved')}</option>
                        <option value="rejected">{t('timesheets.rejected')}</option>
                    </select>
                </div>
                {showUserColumn && (
                    <div className="filter-group">
                        <label htmlFor="user-filter">
                            {t('timesheets.employee')}
                        </label>
                        <select
                            id="user-filter"
                            className="filter-select"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                            aria-label="Filter op werknemer"
                        >
                            <option value="">{t('timesheets.allEmployees')}</option>
                            {allUserOptions.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="filter-group">
                    <label htmlFor="project-filter">
                        {t('timesheets.project')}
                    </label>
                    <select
                        id="project-filter"
                        className="filter-select"
                        value={filterProject}
                        onChange={e => setFilterProject(e.target.value)}
                        aria-label="Filter op project"
                    >
                        <option value="">{t('timesheets.allProjects')}</option>
                        {allProjectOptions.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
            </div>
        </section>
    );

    if (loading) return <Loader message={t('common.loading')} />;
    if (error) return <NoDataConstruction text={t('common.error')}/>;

    // Show all voorman projects even if no timesheets
    if (
        filteredTimesheets.length === 0 &&
        projectRole === "voorman" &&
        Array.isArray(voormanProjects) &&
        voormanProjects.length > 0
    ) {
        return (
            <div className="timesheet-form">
                <h1>{t('timesheets.overview')}</h1>
                {(!userId || userId === auth?.userId) && (
                    <div className="create-timesheet-wrapper mb-4">
                        <Link to="/timesheets/new" className="btn btn-add">
                            <span aria-hidden="true">➕</span> {t('timesheets.newTimesheet')}
                        </Link>
                    </div>
                )}
                {Filters}
                <p className="notifications__empty">{t('timesheets.foremanProjectsNoTimesheets', { status: getStatusText(filterStatus) })}</p>
                <ul className="voorman-projects-list">
                    {voormanProjects.map((project) => (
                        <li key={project.id}>{project.name}</li>
                    ))}
                </ul>
            </div>
        );
    }

    if (filteredTimesheets.length === 0 && (projectRole === null && !isOwnerOrFinance(auth?.role))) {
        return (
            <div className="timesheet-form">
                <h1>{t('timesheets.overview')}</h1>
                {(!userId || userId === auth?.userId) && (
                    <div className="create-timesheet-wrapper mb-4">
                        <Link to="/timesheets/new" className="btn btn-add">
                            <span aria-hidden="true">➕</span> {t('timesheets.newTimesheet')}
                        </Link>
                    </div>
                )}
                {Filters}
                <NoDataConstruction text={t('timesheets.noTimesheetsAndNoProject')}/>
            </div>
        );
    }

    // Show message if no pending timesheets after filtering, but always show filters
    if (filteredTimesheets.length === 0) {
        const statusLabel = getStatusText(filterStatus);
        const noDataText = statusLabel
            ? t('timesheets.noTimesheetsWithStatus', { status: statusLabel })
            : t('timesheets.noTimesheetsFound');
        return (
            <div className="timesheet-form">
                <h1>{t('timesheets.overview')}</h1>
                {(!userId || userId === auth?.userId) && (
                    <div className="create-timesheet-wrapper mb-4">
                        <Link to="/timesheets/new" className="btn btn-add">
                             {t('timesheets.newTimesheet')}
                        </Link>
                    </div>
                )}
                {Filters}
                <NoDataConstruction text={noDataText}/>
            </div>
        );
    }
    return (
        <div className="timesheet-list">
            {/* Header with count */}
            <div className="list-header">
                <div className="list-header-left">
                    <h2>{t('timesheets.overview')}</h2>
                    <span className="count-badge">{filteredTimesheets.length}</span>
                </div>
                {(!userId || userId === auth?.userId) && (
                    <Link to="/timesheets/new" className="btn btn-add">
                        {t('timesheets.newTimesheet')}
                    </Link>
                )}
            </div>

            {Filters}

            {filteredTimesheets.length > 0 && (
                <div className="results-summary" role="status" aria-live="polite">
                    <span className="results-count">
                        {filteredTimesheets.length} {filteredTimesheets.length === 1 ? 'urenstaat' : 'urenstaten'}
                    </span>
                    {search && (
                        <span className="results-meta">
                            (gefilterd van {visibleTimesheets.length})
                        </span>
                    )}
                </div>
            )}

            <div className="cards-grid">
                {Object.keys(groupedByProject).map(pid => {
                    const timesheets = groupedByProject[pid] || [];
                    const project = timesheets[0]?.project || (voormanProjects && voormanProjects.find(p => String(p.id) === pid));
                    return timesheets.map(ts => (
                        <article className="card" key={ts.id}>
                            <div className="card-header">
                                <div className="card-icon">📋</div>
                                <div className="timesheet-header-info">
                                    <h3>{project?.name || t('common.unknownProject')}</h3>
                                    <div className="timesheet-meta">
                                        <span className="timesheet-meta-item">
                                            <strong>Week:</strong> {ts.week_number}, {ts.year}
                                        </span>
                                        {showUserColumn && (
                                            <span className="timesheet-meta-item">
                                                <strong>{t('timesheets.employee')}:</strong> {ts.user.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {getStatusBadge(ts.status)}
                            </div>

                            <div className="card-details">
                                {showUserColumn && (
                                    <div className="card-detail-item">
                                        <strong>{t('timesheets.employee')}:</strong>
                                        <span>{ts.user.name}</span>
                                    </div>
                                )}
                                {showProjectColumn && (
                                    <div className="card-detail-item">
                                        <strong>{t('timesheets.project')}:</strong>
                                        <span>{ts.project.name}</span>
                                    </div>
                                )}
                                <div className="card-detail-item">
                                    <strong>{t('timesheets.week')}:</strong>
                                    <span>Week {ts.week_number}, {ts.year}</span>
                                </div>
                            </div>

                            <div className="card-actions" style={{display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-color)'}}>
                                <Link
                                    to={`/timesheets/${ts.id}`}
                                    className="btn btn-primary btn-small"
                                    aria-label={`${t('common.view')} urenstaat ${project?.name} week ${ts.week_number}`}
                                >
                                    {t('common.view')}
                                </Link>

                                {/* Edit button for draft timesheets */}
                                {ts.status === "draft" && ts.user_id === auth.userId && (
                                    <Link
                                        to={`/timesheets/${ts.id}/edit`}
                                        className="btn btn-edit btn-small"
                                        aria-label={`${t('common.edit')} urenstaat`}
                                    >
                                        {t('common.edit')}
                                    </Link>
                                )}

                                {ts.status === "rejected" && ts.user_id === auth.userId && (
                                    <Link
                                        to={`/timesheets/${ts.id}/edit`}
                                        className="btn btn-edit btn-small"
                                        aria-label={`${t('common.edit')} afgekeurde urenstaat`}
                                    >
                                        {t('common.edit')}
                                    </Link>
                                )}

                                {((projectRole === "voorman" && ts.status === "pending" && ts.user_id !== auth.userId) ||
                                    (isOwnerOrFinance(auth.role) && ["pending", "approved_by_voorman"].includes(ts.status))) && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-small"
                                            onClick={() => handleApprove(ts.id)}
                                            aria-label={`${t('timesheets.approve')} urenstaat`}
                                        >
                                            {t('timesheets.approve')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-cancel btn-small"
                                            onClick={() => handleReject(ts.id)}
                                            aria-label={`${t('timesheets.reject')} urenstaat`}
                                        >
                                            {t('timesheets.reject')}
                                        </button>
                                    </>
                                )}

                                {isOwnerOrFinance(auth.role) && (
                                    <button
                                        type="button"
                                        className="btn btn-delete btn-small"
                                        onClick={() => confirmDelete(ts.id)}
                                        aria-label={`${t('common.delete')} urenstaat`}
                                    >
                                        {t('common.delete')}
                                    </button>
                                )}
                            </div>
                        </article>
                    ));
                })}
            </div>
            <StyledModalView
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title={t('timesheets.confirmDelete')}
                confirmText={t('timesheets.yesDelete')}
                closeText={t('common.cancel')}
                onConfirm={handleDeleteConfirmed}
            >
                <p>{t('timesheets.deleteWarning')}</p>
            </StyledModalView>
        </div>
    );
}

TimesheetListView.propTypes = {
    timesheets: PropTypes.array.isRequired,
    allTimesheets: PropTypes.array,
    filterStatus: PropTypes.string,
    setFilterStatus: PropTypes.func.isRequired,
    filterUser: PropTypes.string,
    setFilterUser: PropTypes.func.isRequired,
    sortOrder: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
    projectRole: PropTypes.string,
    showDeleteModal: PropTypes.bool.isRequired,
    setShowDeleteModal: PropTypes.func.isRequired,
    confirmDelete: PropTypes.func.isRequired,
    handleDeleteConfirmed: PropTypes.func.isRequired,
    handleApprove: PropTypes.func.isRequired,
    handleReject: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired,
    userId: PropTypes.number,
    voormanProjects: PropTypes.array,
    filterProject: PropTypes.string,
    setFilterProject: PropTypes.func,
};

export default TimesheetListView;
